/**
 * Leads API
 * All lead-related operations: CRUD, search, messages, bulk operations
 */

import { crmGet, crmPost, crmPut, crmDelete } from './crmConfig';

// ─── Fetch & Search ───────────────────────────────────────────────────────────

/**
 * Fetch leads with filters and pagination
 * @param {Object} params - Query parameters (patientStatus, page, limit, search, stage, from, to, statusFilter, sourceFilter, tagFilter, sortBy, assigneeFilter)
 */
export const fetchLeads = async (params) => {
  const response = await crmGet('/api/leads', params);
  return response.data;
};

/**
 * Search leads by assignee
 * @param {Object} params - Query parameters (same as fetchLeads)
 */
export const searchLeadsByAssignee = async (params) => {
  const response = await crmGet('/api/leads/searchAssignee', params);
  return response.data;
};

/**
 * Search leads (general search)
 * @param {Object} params - Query parameters
 */
export const searchLeads = async (params) => {
  const response = await crmGet('/api/leads/search', params);
  return response.data;
};

// ─── Single Lead Operations ───────────────────────────────────────────────────

/**
 * Fetch single lead by ID
 * @param {string} id - Lead ID
 * @param {string} patientStatus - Optional patient status (defaults to 'new')
 */
export const fetchLead = async (id, patientStatus = 'new') => {
  const path = patientStatus
    ? `/api/leads/${id}/${patientStatus}`
    : `/api/leads/${id}/new`;
  const response = await crmGet(path);
  return response.data;
};

/**
 * Create a new lead
 */
export const createLead = async (data) => {
  const response = await crmPost('/api/leads/', data);
  return response.data;
};

/**
 * Update a lead
 * @param {string} id - Lead ID
 * @param {Object} data - Lead data to update
 */
export const updateLead = async (id, data) => {
  const response = await crmPut(`/api/leads/${id}/new`, data);
  return response.data;
};

/**
 * Delete a lead
 * @param {string} id - Lead ID
 */
export const deleteLead = async (id) => {
  const response = await crmDelete(`/api/leads/${id}/new`);
  return response.data;
};

// ─── Lead Actions ─────────────────────────────────────────────────────────────

/**
 * Toggle lead favorite status
 * @param {string} id - Lead ID
 */
export const toggleFavourite = async (id) => {
  const response = await crmPut(`/api/leads/${id}/favourite/new`);
  return response.data;
};

/**
 * Mark lead as read
 * @param {string} id - Lead ID
 */
export const markLeadAsRead = async (id) => {
  const response = await crmPut(`/api/leads/markAsRead/${id}/new`);
  return response.data;
};

/**
 * Update latest message read status
 * @param {string} id - Lead ID
 */
export const updateLatestReadStatus = async (id) => {
  const response = await crmPut(`/api/leads/${id}/new/latest/readStatus`);
  return response.data;
};

/**
 * Promote lead to patient
 * @param {string} id - Lead ID
 */
export const promoteLead = async (id) => {
  const response = await crmPost(`/api/leads/${id}/promote`);
  return response.data;
};

/**
 * Fetch leads in the onboarding stage (dedicated lightweight endpoint)
 * @param {Object} params - Query parameters (limit, search)
 */
export const fetchOnboardingLeads = async (params) => {
  const response = await crmGet('/api/leads/onboarding', params);
  return response.data;
};

/**
 * Update onboarding checklist
 * @param {string} id - Lead ID
 * @param {Object} data - Checklist data
 */
export const updateOnboardingChecklist = async (id, data) => {
  const response = await crmPut(`/api/leads/${id}/onboarding-checklist`, data);
  return response.data;
};

/**
 * Convert an onboarding lead to a full patient profile
 * @param {string} id - Lead ID
 */
export const convertOnboardingLead = async (id) => {
  const response = await crmPost(`/api/leads/${id}/convert`);
  return response.data;
};

/**
 * Send onboarding form (intake or consent) to lead via email
 * @param {string} id - Lead ID
 * @param {string} formType - 'intake' or 'consent'
 */
export const sendOnboardingForm = async (id, formType) => {
  const response = await crmPost(`/api/leads/${id}/send-form`, { formType });
  return response.data;
};

// ─── Message Operations ───────────────────────────────────────────────────────

/**
 * Delete a message
 * @param {string} id - Lead ID
 * @param {string} dateTime - Message dateTime
 */
export const deleteMessage = async (id, dateTime) => {
  const response = await crmDelete(`/api/leads/${id}/new/messages`, { dateTime });
  return response.data;
};

/**
 * Mark message as unread
 * @param {string} id - Lead ID
 * @param {string} dateTime - Message dateTime
 */
export const markMessageAsUnread = async (id, dateTime) => {
  const response = await crmPut(`/api/leads/${id}/new/messages/mark-unread`, { dateTime });
  return response.data;
};

// ─── Bulk Operations ──────────────────────────────────────────────────────────

/**
 * Merge multiple leads
 * @param {string} primaryLeadId - Primary lead to keep
 * @param {string[]} secondaryLeadIds - Leads to merge into primary
 */
export const mergeLeads = async (primaryLeadId, secondaryLeadIds) => {
  const response = await crmPost('/api/leads/merge', {
    primaryLeadId,
    secondaryLeadIds
  });
  return response.data;
};
